import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthSession, PropertyListing, PropertySnapshot } from '../types';
import './RealEstateDashboard.css';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const formatCurrency = (value: number | null) => {
  if (!value) return 'Não informado';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  }).format(value);
};

const formatDateTime = (value?: string) => {
  if (!value) return 'Sem atualização';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
};

const getPricePerMeter = (listing: PropertyListing) => {
  if (!listing.price || !listing.area) return null;
  return Math.round(listing.price / listing.area);
};

interface RealEstateDashboardProps {
  session: AuthSession | null;
}

const RealEstateDashboard: React.FC<RealEstateDashboardProps> = ({ session }) => {
  const [snapshot, setSnapshot] = useState<PropertySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [city, setCity] = useState('Todas');
  const [bedrooms, setBedrooms] = useState('Todos');
  const [sortBy, setSortBy] = useState('priceAsc');
  const [query, setQuery] = useState('');

  const loadProperties = useCallback(async (force = false) => {
    setError('');
    force ? setRefreshing(true) : setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/properties${force ? '/refresh' : ''}`, {
        method: force ? 'POST' : 'GET',
        headers: {
          Authorization: `Bearer ${session?.token || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('O serviço de imóveis não respondeu como esperado.');
      }

      const data = await response.json();
      setSnapshot(data);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Falha ao carregar imóveis.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [session?.token]);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const filteredListings = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR');

    return [...(snapshot?.listings || [])]
      .filter((listing) => city === 'Todas' || listing.city === city)
      .filter((listing) => bedrooms === 'Todos' || String(listing.bedrooms) === bedrooms)
      .filter((listing) => {
        if (!normalizedQuery) return true;
        return `${listing.title} ${listing.neighborhood} ${listing.description}`
          .toLocaleLowerCase('pt-BR')
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (sortBy === 'priceDesc') return (b.price || 0) - (a.price || 0);
        if (sortBy === 'areaDesc') return (b.area || 0) - (a.area || 0);
        if (sortBy === 'meterAsc') return (getPricePerMeter(a) || Infinity) - (getPricePerMeter(b) || Infinity);
        return (a.price || Infinity) - (b.price || Infinity);
      });
  }, [bedrooms, city, query, snapshot, sortBy]);

  const cityOptions = useMemo(() => {
    const cities = new Set((snapshot?.listings || []).map((listing) => listing.city));
    return ['Todas', ...Array.from(cities).sort()];
  }, [snapshot]);

  const bedroomOptions = useMemo(() => {
    const values = new Set(
      (snapshot?.listings || [])
        .map((listing) => listing.bedrooms)
        .filter((value): value is number => Boolean(value))
    );
    return ['Todos', ...Array.from(values).sort((a, b) => a - b).map(String)];
  }, [snapshot]);

  return (
    <section className="real-estate-page">
      <div className="real-estate-container">
        <header className="real-estate-header">
          <div>
            <span className="dashboard-kicker">Pós-login</span>
            <h1>Radar de apartamentos</h1>
            <p>
              Lista diária de apartamentos à venda no Gama e em Santa Maria, consolidada por um agente
              de coleta com cache local.
            </p>
          </div>

          <button
            type="button"
            className="refresh-button"
            onClick={() => loadProperties(true)}
            disabled={refreshing || !session?.user.permissions.includes('properties:refresh')}
          >
            <i className={`fas fa-rotate ${refreshing ? 'fa-spin' : ''}`}></i>
            {refreshing ? 'Atualizando' : 'Atualizar agora'}
          </button>
        </header>

        {error && (
          <div className="dashboard-alert error-alert">
            <i className="fas fa-triangle-exclamation"></i>
            {error}
          </div>
        )}

        {snapshot?.demoMode && (
          <div className="dashboard-alert warning-alert">
            <i className="fas fa-circle-info"></i>
            As fontes externas não retornaram imóveis nesta execução. A tela está exibindo registros de demonstração.
          </div>
        )}

        <div className="summary-grid">
          <article className="summary-card">
            <span>Total encontrado</span>
            <strong>{snapshot?.summary.total ?? '-'}</strong>
          </article>
          <article className="summary-card">
            <span>Gama</span>
            <strong>{snapshot?.summary.totalsByCity?.Gama ?? 0}</strong>
          </article>
          <article className="summary-card">
            <span>Santa Maria</span>
            <strong>{snapshot?.summary.totalsByCity?.['Santa Maria'] ?? 0}</strong>
          </article>
          <article className="summary-card">
            <span>Atualizado em</span>
            <strong>{formatDateTime(snapshot?.collectedAt)}</strong>
          </article>
        </div>

        <div className="dashboard-toolbar">
          <label>
            Buscar
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Condomínio, bairro, descrição"
            />
          </label>

          <label>
            Cidade
            <select value={city} onChange={(event) => setCity(event.target.value)}>
              {cityOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>

          <label>
            Quartos
            <select value={bedrooms} onChange={(event) => setBedrooms(event.target.value)}>
              {bedroomOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>

          <label>
            Ordenar
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="priceAsc">Menor preço</option>
              <option value="priceDesc">Maior preço</option>
              <option value="areaDesc">Maior metragem</option>
              <option value="meterAsc">Menor R$/m²</option>
            </select>
          </label>
        </div>

        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            Carregando apartamentos...
          </div>
        ) : (
          <div className="listings-grid">
            {filteredListings.map((listing) => {
              const pricePerMeter = getPricePerMeter(listing);

              return (
                <article className="listing-card" key={listing.id}>
                  <div className="listing-image">
                    {listing.imageUrl ? (
                      <img src={listing.imageUrl} alt={listing.title} loading="lazy" />
                    ) : (
                      <i className="fas fa-building"></i>
                    )}
                  </div>

                  <div className="listing-body">
                    <div className="listing-heading">
                      <span>{listing.city} · {listing.neighborhood}</span>
                      <h2>{listing.title}</h2>
                    </div>

                    <div className="listing-price-row">
                      <strong>{formatCurrency(listing.price)}</strong>
                      <span>{pricePerMeter ? `${formatCurrency(pricePerMeter)}/m²` : 'R$/m² não informado'}</span>
                    </div>

                    <div className="listing-facts">
                      <span><i className="fas fa-bed"></i>{listing.bedrooms || '-'} quartos</span>
                      <span><i className="fas fa-ruler-combined"></i>{listing.area || '-'} m²</span>
                      <span><i className="fas fa-receipt"></i>{formatCurrency(listing.condoFee)}</span>
                    </div>

                    <p>{listing.description || 'Sem descrição detalhada na fonte.'}</p>

                    <div className="listing-footer">
                      <span>{listing.source}</span>
                      {listing.url ? (
                        <a href={listing.url} target="_blank" rel="noreferrer">
                          Abrir anúncio
                          <i className="fas fa-up-right-from-square"></i>
                        </a>
                      ) : (
                        <span>Sem link externo</span>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && filteredListings.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-magnifying-glass"></i>
            Nenhum imóvel encontrado com os filtros atuais.
          </div>
        )}

        {!!snapshot?.errors?.length && (
          <div className="source-errors">
            <h2>Fontes com aviso</h2>
            {snapshot.errors.map((sourceError) => (
              <p key={`${sourceError.source}-${sourceError.city}`}>
                {sourceError.source} em {sourceError.city}: {sourceError.message}
              </p>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RealEstateDashboard;
